/**
 * Recipe + RecipeVersion + RecipeIngredient + RecipeStep schemas.
 *
 * Immutable versioning invariant: a recipe_version (and its ingredients + steps) is a
 * frozen snapshot. The read shapes carry created_at (immutable); there are no update
 * inputs for version content — a change creates a NEW version via RecipeVersionCreateInput.
 */

import { z } from 'zod';
import {
  RECIPE_STEP_CONTROL_TYPES,
  RECIPE_TYPES,
} from '../enums/index.js';
import {
  uuid,
  nonEmptyString,
  timestamptz,
  quantity,
  nonNegativeQuantity,
  money,
  percent,
  tolerancePct,
  jsonObject,
  softDeleteFields,
  timestampFields,
} from './common.js';

// ── Recipe (mutable head pointer) ────────────────────────────────────────────

export const Recipe = z
  .object({
    id: uuid,
    tenant_id: uuid,
    name: nonEmptyString,
    output_article_id: uuid,
    active_version_id: uuid.nullable(),
    recipe_type: z.enum(RECIPE_TYPES),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type Recipe = z.infer<typeof Recipe>;

export const RecipeCreateInput = z
  .object({
    name: nonEmptyString,
    output_article_id: uuid,
    /**
     * Opcional perquè la immensa majoria de receptes són normals i el servidor ja
     * hi posa el valor per defecte: una alta que no en parli no ha d'haver de
     * dir-ho. Quan hi és, mana.
     */
    recipe_type: z.enum(RECIPE_TYPES).optional(),
  })
  .strict();
export type RecipeCreateInput = z.infer<typeof RecipeCreateInput>;

// ── RecipeIngredient (frozen BOM line) ────────────────────────────────────────

export const RecipeIngredient = z
  .object({
    id: uuid,
    tenant_id: uuid,
    recipe_version_id: uuid,
    ingredient_article_id: uuid,
    quantity: nonNegativeQuantity,
    unit_id: uuid,
    sort_order: z.number().int().nonnegative(),
    tolerance_pct: tolerancePct.nullable(),
    /**
     * El pas de la mateixa versió en què s'incorpora l'ingredient (OBR-17). NULL = no
     * s'indica: l'ingredient segueix sent una línia del BOM global, com sempre. Un
     * ingredient s'incorpora en UN sol pas.
     */
    recipe_step_id: uuid.nullable(),
    created_at: timestamptz,
  })
  .strict();
export type RecipeIngredient = z.infer<typeof RecipeIngredient>;

/** BOM line as supplied when creating a NEW version. */
export const RecipeIngredientInput = z
  .object({
    ingredient_article_id: uuid,
    quantity: nonNegativeQuantity,
    unit_id: uuid,
    sort_order: z.number().int().nonnegative().default(0),
    /** Per-line weighing tolerance; null/absent inherits the obrador default. */
    tolerance_pct: tolerancePct.nullable().default(null),
    /** El pas en què s'incorpora (id d'un dels `steps` de la mateixa versió); null = cap. */
    recipe_step_id: uuid.nullable().default(null),
  })
  .strict();
export type RecipeIngredientInput = z.infer<typeof RecipeIngredientInput>;

// ── RecipeOutput (frozen additional/by-product output) ────────────────────────

/**
 * An ADDITIONAL product a recipe version produces, besides its primary output
 * (recipes.output_article_id + recipe_versions.base_yield_quantity/unit). Most
 * recipes have none; some produce a by-product alongside the main article (e.g.
 * trimmings sold as a second SKU). Frozen with the version, same as the BOM.
 */
export const RecipeOutput = z
  .object({
    id: uuid,
    tenant_id: uuid,
    recipe_version_id: uuid,
    article_id: uuid,
    quantity: quantity.positive(),
    unit_id: uuid,
    sort_order: z.number().int().nonnegative(),
    created_at: timestamptz,
  })
  .strict();
export type RecipeOutput = z.infer<typeof RecipeOutput>;

/** An additional output as supplied when creating a NEW version. */
export const RecipeOutputInput = z
  .object({
    article_id: uuid,
    quantity: quantity.positive(),
    unit_id: uuid,
    sort_order: z.number().int().nonnegative().default(0),
  })
  .strict();
export type RecipeOutputInput = z.infer<typeof RecipeOutputInput>;

// ── RecipeResource (a resource the elaboració occupies) ───────────────────────

/**
 * A Recurs this recipe version needs: a forn, an operari, an amassadora — anything
 * an elaboració occupies that is not matèria primera (see the resources master in
 * the settings schema). Declared per recipe so costing (Onada 2) has the inputs it
 * needs; Onada 1 stores and displays it and computes nothing.
 *
 * Since OBR-45 a line asks for a CAPACITY — a resource TYPE («1 × Amassadora») — not
 * a machine: which Amassadora (01 or 02) is chosen when the work order is planned
 * (work_order_resources). `resource_id` survives only on the lines of versions
 * frozen before that, as history; new lines never carry it.
 *
 * quantity + duration_minutes are the recipe-specific values (exactly as
 * recipe_ingredients.quantity is recipe-specific against a live article).
 *
 * recipe_step_id (OBR-17): el pas que necessita el recurs («1 × Amassadora» al pas
 * Amassat). NULL = recurs de tota la recepta: les línies anteriors a OBR-17, que es
 * mantenen tal com eren fins que algú les assigni a un pas.
 */
export const RecipeResource = z
  .object({
    id: uuid,
    tenant_id: uuid,
    recipe_version_id: uuid,
    resource_type_id: uuid,
    /** History only: the machine a pre-OBR-45 version pinned. */
    resource_id: uuid.nullable(),
    quantity: quantity.positive(),
    duration_minutes: z.number().int().nonnegative().nullable(),
    recipe_step_id: uuid.nullable(),
    sort_order: z.number().int().nonnegative(),
    created_at: timestamptz,
  })
  .strict();
export type RecipeResource = z.infer<typeof RecipeResource>;

/** A resource line as supplied when creating a NEW version: a type, never a machine. */
export const RecipeResourceInput = z
  .object({
    resource_type_id: uuid,
    quantity: quantity.positive(),
    duration_minutes: z.number().int().nonnegative().optional(),
    sort_order: z.number().int().nonnegative().default(0),
    /** El pas que el necessita (id d'un dels `steps` de la mateixa versió); null = tota la recepta. */
    recipe_step_id: uuid.nullable().default(null),
  })
  .strict();
export type RecipeResourceInput = z.infer<typeof RecipeResourceInput>;

// ── RecipeStep (frozen elaboration step) ──────────────────────────────────────

export const RecipeStep = z
  .object({
    id: uuid,
    tenant_id: uuid,
    recipe_version_id: uuid,
    step_number: z.number().int().positive(),
    /**
     * El Procés (mestre) del qual neix el pas, per traçabilitat. NULL = pas escrit a mà,
     * sense Procés: el Procés és opcional (OBR-17).
     */
    process_id: uuid.nullable(),
    /** El nom del pas («Amassat inicial»): editable a la recepta. */
    description: nonEmptyString,
    /** Instruccions del pas, en text lliure. */
    instructions: z.string().nullable(),
    estimated_duration_minutes: z.number().int().nonnegative().nullable(),
    temperature_c: percent.nullable(),
    control_type: z.enum(RECIPE_STEP_CONTROL_TYPES),
    is_blocking: z.boolean(),
    pcc_config_json: jsonObject.nullable(),
    created_at: timestamptz,
  })
  .strict();
export type RecipeStep = z.infer<typeof RecipeStep>;

/**
 * Step as supplied when creating a NEW version (OBR-17).
 *
 * UN PAS ÉS UNA INSTÀNCIA EDITABLE. Pot néixer d'un Procés del mestre (screen 23 ·
 * Configuració · Processos): llavors nom, instruccions, durada, temperatura, control i
 * recursos es copien com a valors INICIALS, i a partir d'aquí són de la recepta —
 * s'adapten sense tocar el mestre, i un canvi posterior al Procés no altera receptes ja
 * fetes (instantània congelada amb la versió, igual que recipe_ingredients.quantity).
 * `process_id` només diu de quin Procés va néixer (traçabilitat) i és OPCIONAL: un pas
 * es pot escriure a mà.
 *
 * `id` el posa el client perquè els ingredients i els recursos de la mateixa versió
 * puguin apuntar al pas en el mateix desat (`recipe_step_id`); si no hi és, el servidor
 * n'assigna un. PCC steps (control_type='pcc') may be blocking; only a PCC step may set
 * is_blocking=true (the only blocking case in O1 — a PCC step blocks execution; every
 * other warning never blocks).
 */
export const RecipeStepInput = z
  .object({
    id: uuid.optional(),
    step_number: z.number().int().positive(),
    process_id: uuid.nullable().default(null),
    description: nonEmptyString,
    instructions: z.string().trim().min(1).max(4000).optional(),
    estimated_duration_minutes: z.number().int().nonnegative().optional(),
    temperature_c: percent.optional(),
    control_type: z.enum(RECIPE_STEP_CONTROL_TYPES).default('none'),
    is_blocking: z.boolean().default(false),
  })
  .strict()
  .refine((s) => !s.is_blocking || s.control_type === 'pcc', {
    message: 'only a PCC step may be blocking (is_blocking requires control_type="pcc")',
    path: ['is_blocking'],
  });
export type RecipeStepInput = z.infer<typeof RecipeStepInput>;

// ── RecipeVersion (immutable snapshot) ────────────────────────────────────────

export const RecipeVersion = z
  .object({
    id: uuid,
    tenant_id: uuid,
    recipe_id: uuid,
    version_number: z.number().int().positive(),
    base_yield_quantity: quantity.positive(),
    base_yield_unit_id: uuid,
    lead_time_days: z.number().int().nonnegative(),
    standard_cost: money.nullable(),
    consolidated_allergens: z.array(z.unknown()),
    notes: z.string().trim().min(1).nullable(),
    sequential_preparation: z.boolean(),
    created_at: timestamptz,
    created_by: uuid.nullable(),
  })
  .strict();
export type RecipeVersion = z.infer<typeof RecipeVersion>;

/**
 * Create a NEW immutable recipe version (the only way to "edit" a recipe). The server
 * assigns version_number monotonically and derives consolidated_allergens from the BOM
 * (never client-supplied). At least one ingredient is required.
 */
export const RecipeVersionCreateInput = z
  .object({
    recipe_id: uuid,
    base_yield_quantity: quantity.positive(),
    base_yield_unit_id: uuid,
    lead_time_days: z.number().int().nonnegative().default(0),
    notes: z.string().trim().min(1).optional(),
    /** One ingredient at a time, in sort_order, during execution (spec §1). */
    sequential_preparation: z.boolean().default(false),
    ingredients: z.array(RecipeIngredientInput).min(1),
    steps: z.array(RecipeStepInput).default([]),
    outputs: z.array(RecipeOutputInput).default([]),
  })
  .strict();
export type RecipeVersionCreateInput = z.infer<typeof RecipeVersionCreateInput>;
